import React, { useEffect, useState } from 'react';

import { useGetNotFoundOrderIds } from 'hooks/useGetNotFoundOrderIds';
import { OrdersReportFilterFormValues } from 'pages/Finance/OrdersReport/OrdersReportList/OrdersReportListFilter/OrdersReportListFilter';
import { OrdersReportListFilter } from 'pages/Finance/OrdersReport/OrdersReportList/OrdersReportListFilter/OrdersReportListFilter';
import { OrdersReportListFilterChips } from 'pages/Finance/OrdersReport/OrdersReportList/OrdersReportListFilter/OrdersReportListFilterChips';
import { useListContext, WithListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { ListExportAlert } from 'shared/mui/Alert/ListExportAlert/ListExportAlert';
import { SuccessReportAlert } from 'shared/mui/Alert/SuccessReportAlert/SuccessReportAlert';
import { Box } from 'shared/mui/Box';
import { getAppliedTimezone } from 'shared/mui/DatePicker/utils/getAppliedTimezone';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterIdButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterIdChip } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterFavorite } from 'shared/mui/Filter/FilterFavorite/FilterFavorite';
import { FilterIdDialog } from 'shared/mui/Filter/FilterId/FilterIdDialog/FilterIdDialog';
import { FilterSaveFavorite } from 'shared/mui/Filter/FilterSaveFavorite/FilterSaveFavorite';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { FilterIdTypes, useIdFilters } from 'shared/mui/Filter/hooks/useIdFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { Order } from 'types/embedded/order';
import { OrderReportData } from 'types/embedded/orderReport';
import { cleanEmpty } from 'utils/cleanEmpty';
import { formatInputToTimestamp } from 'utils/date';

import { OrdersReportListLocalDatagrid } from './OrdersReportListLocalDatagrid/OrdersReportLocalDatagrid/OrdersReportListLocalDatagrid';
import { OrdersReportListNotFoundOrdersAlert } from './OrdersReportListNotFoundOrdersAlert/OrdersReportListNotFoundOrdersAlert';

type Props = {
  isFiltersApplied: boolean;
  setIsOpenNotifyDialog: (v: boolean) => void;
};

const FILTER_RESET_VALUES: any = {
  appliedDate: [null, null],
  transactionsAppliedDate: [null, null],
  company: null,
  merchant: null,
  provider: null,
  gateway: [],
  method: [],
  direction: null,
  currency: [],
  timezone: null,
};

export const OrdersReportListContent: React.FC<Props> = ({
  isFiltersApplied,
  setIsOpenNotifyDialog,
}) => {
  const { filterValues } = useListContext();
  const [currentIdFilter, setCurrentIdFilter] = useState<FilterIdTypes>(FilterIdTypes.Id);
  const [isOpenIdDialog, setIsOpenIdDialog] = useState<boolean>(false);
  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<OrdersReportFilterFormValues>({
      mode: 'all',
      defaultValues: {
        merchant: filterValues?.merchant || null,
        appliedDate: [
          formatInputToTimestamp(filterValues?.createdAt?.gte),
          formatInputToTimestamp(filterValues?.createdAt?.lte),
        ],
        transactionsAppliedDate: [
          formatInputToTimestamp(filterValues?.transactions?.createdAt?.gte),
          formatInputToTimestamp(filterValues?.transactions?.createdAt?.lte),
        ],
        timezone: filterValues?.timezone || null,
        company: filterValues?.company || null,
        direction: filterValues?.direction || null,
        gateway: filterValues?.gateway || [],
        provider: filterValues?.provider || null,
        merchantAccount: filterValues?.merchantAccount || [],
        currency: filterValues?.currency || [],
        method: filterValues?.method || [],
      },
      resetValues: FILTER_RESET_VALUES,
    });

  const {
    setAppliedFilter: setAppliedIdFilter,
    onSubmit: onSubmitIds,
    onReset: onResetIds,
    appliedIdFilters,
    form: formId,
  } = useIdFilters({
    defaultValues: {
      merchantReference: filterValues?.merchantReference || [],
      id: filterValues?.id || [],
      providerReferenceId: filterValues?.providerReferenceId || [],
    },
    resetValues: {
      merchantReference: [],
      id: [],
      providerReferenceId: [],
    },
  });

  useSyncFilter<OrdersReportFilterFormValues>({
    appliedFilters,
    appliedIdFilters,
    transform: (appliedFilters) => {
      const {
        appliedDate = [null, null],
        transactionsAppliedDate = [null, null],
        timezone,
        ...restFilters
      } = appliedFilters;
      return cleanEmpty({
        ...restFilters,
        createdAt: {
          gte: formatInputToTimestamp(appliedDate?.[0], { unit: 'seconds' }),
          lte: formatInputToTimestamp(appliedDate?.[1], { unit: 'seconds' }),
        },
        transactions: {
          createdAt: {
            gte: formatInputToTimestamp(transactionsAppliedDate?.[0], { unit: 'seconds' }),
            lte: formatInputToTimestamp(transactionsAppliedDate?.[1], { unit: 'seconds' }),
          },
        },
        timezone: getAppliedTimezone(timezone, appliedDate, transactionsAppliedDate),
      });
    },
  });

  const formValuesWatched = form.watch();
  const idsFormValuesWatched = formId.watch();
  const isFormValuesExist = Object.values(cleanEmpty(formValuesWatched)).length > 0;
  const isIdFormValuesExist = Object.values(cleanEmpty(idsFormValuesWatched)).length > 0;

  const [showAlert, setShowAlert] = useState(true);
  const [showExportAlert, setShowExportAlert] = useState(true);

  useEffect(() => {
    setShowExportAlert(true);
    setShowAlert(true);
  }, [appliedFilters, appliedIdFilters]);

  return (
    <WithListContext
      render={({ isLoading, data: orders, total, isFetching }) => {
        const notFoundIds = appliedIdFilters.id?.filter(
          (id) => id && !orders?.some((order) => order?.originId === id),
        );
        const notFoundMerchantIds = appliedIdFilters.merchantReference?.filter(
          (id) => !orders?.some((order) => order?.merchantReference === id),
        );
        const notFoundProviderIds = appliedIdFilters.providerReferenceId?.filter(
          (id) => !orders?.some((order) => order?.providerReferenceId === id),
        );

        const notFoundOrdersReportIds = useGetNotFoundOrderIds(orders as Order[], filterValues);

        function renderAlert() {
          if (isFetching) {
            return <></>;
          }
          if ((total || 0) >= 1001 && isFiltersApplied && showExportAlert) {
            return (
              <ListExportAlert
                onClick={() => setIsOpenNotifyDialog(true)}
                onClose={() => setShowExportAlert(false)}
                sx={{ margin: '4px 0 10px 0' }}
              />
            );
          }

          return (
            <OrdersReportListNotFoundOrdersAlert
              filterValues={filterValues}
              isLoading={!!isLoading}
              notFoundOrdersReportIds={notFoundOrdersReportIds}
            />
          );
        }

        function renderPeriodAlert() {
          if (showAlert && filterValues.createdAt && !isFetching && total !== 0) {
            return (
              <SuccessReportAlert
                date={filterValues.createdAt}
                onClose={() => setShowAlert(false)}
                timezone={filterValues.timezone}
              />
            );
          }
        }

        return (
          <>
            <FilterToolbar
              leftActionsSlot={[
                <FilterIdButton
                  disabled={isFormValuesExist}
                  key="filter-by-id"
                  onClick={() => {
                    setIsOpenIdDialog(true);
                  }}
                />,
                <FilterButton
                  disabled={isIdFormValuesExist}
                  key="filter"
                  onClick={toggleDrawer(true)}
                />,

                <FilterContextProvider
                  appliedFilters={appliedFilters}
                  key="filter-favourite"
                  setAppliedFilter={setAppliedFilter}
                >
                  <FormProvider {...form}>
                    <FilterFavorite dateFields={['appliedDate', 'transactionsAppliedDate']} />
                  </FormProvider>
                </FilterContextProvider>,
                <FilterColumnsButton
                  disabled={!orders?.length && !isFiltersApplied}
                  key="select-column"
                />,
              ]}
            />
            <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
              <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <FilterDrawerContent>
                    <OrdersReportListFilter />
                  </FilterDrawerContent>
                  <FilterDrawerActionsButtons
                    topSlot={<FilterSaveFavorite disabled={!isFiltersApplied} />}
                  >
                    <FilterResetButton label="Очистить" onReset={onReset} />
                    <FilterApplyButton type="submit" />
                  </FilterDrawerActionsButtons>
                </form>
              </FormProvider>
            </FilterDrawer>
            <FilterChipsToolbar appliedFilters={appliedFilters} appliedIdFilters={appliedIdFilters}>
              <FilterContextProvider
                appliedFilters={appliedFilters}
                setAppliedFilter={setAppliedFilter}
              >
                <FormProvider {...form}>
                  <OrdersReportListFilterChips />
                </FormProvider>
              </FilterContextProvider>
              <FilterContextProvider
                appliedFilters={appliedIdFilters}
                setAppliedFilter={setAppliedIdFilter}
              >
                <FormProvider {...formId}>
                  <FilterIdChip
                    label="ID Kubera"
                    limitTags={2}
                    name="id"
                    onClick={() => {
                      setIsOpenIdDialog(true);
                      setCurrentIdFilter(FilterIdTypes.Id);
                    }}
                  />
                  <FilterIdChip
                    label="ID Мерчантов"
                    limitTags={2}
                    name="merchantReference"
                    onClick={() => {
                      setIsOpenIdDialog(true);
                      setCurrentIdFilter(FilterIdTypes.Merchant);
                    }}
                  />
                  <FilterIdChip
                    label="ID Провайдеров"
                    limitTags={2}
                    name="providerReferenceId"
                    onClick={() => {
                      setIsOpenIdDialog(true);
                      setCurrentIdFilter(FilterIdTypes.Provider);
                    }}
                  />
                  <FilterClearButton
                    key={`clear-${currentIdFilter}`}
                    onClick={() => {
                      onReset();
                      onResetIds(currentIdFilter);
                    }}
                    visible={isFiltersApplied}
                  />
                </FormProvider>
              </FilterContextProvider>
            </FilterChipsToolbar>
            <FormProvider {...formId}>
              <FilterIdDialog
                currentIdFilter={currentIdFilter}
                notFoundIds={notFoundIds}
                notFoundMerchantIds={notFoundMerchantIds}
                notFoundProviderIds={notFoundProviderIds}
                omit={[FilterIdTypes.Any]}
                onChange={(currentFilter) => setCurrentIdFilter(currentFilter)}
                onClose={() => setIsOpenIdDialog(false)}
                onReset={onResetIds}
                onSubmit={onSubmitIds}
                open={isOpenIdDialog}
              />
            </FormProvider>
            <Box>
              {renderAlert()}
              {renderPeriodAlert()}
            </Box>
            <Box
              sx={{
                minHeight: '100%',
                display: 'initial',
              }}
            >
              <OrdersReportListLocalDatagrid
                isLoading={!!isFetching}
                orders={isFiltersApplied && !isFetching ? (orders as OrderReportData[]) || [] : []}
                timezone={filterValues?.timezone}
                toggleDrawer={toggleDrawer}
                total={isFiltersApplied && !isFetching ? total || 0 : 0}
              />
            </Box>
          </>
        );
      }}
    />
  );
};
